import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Lock } from 'lucide-react';
import Blobs from '../components/Blobs';
import { isAuthenticated, getAdminAuthHeaders } from '../utils/auth';
import { useTheme } from '../context/ThemeContext';

export default function Interests({ dark }: { dark: boolean }) {
  const { designMode } = useTheme();
  const isModern = designMode === 'modern';
  const [isAdmin, setIsAdmin] = useState(false);
  const [loadingVerse, setLoadingVerse] = useState(false);

  useEffect(() => {
    setIsAdmin(isAuthenticated());
  }, []);

  const interests = [
    {
      title: "Verse for you",
      description: "Generates random Bible verses for inspiration and reflection.",
      isPublic: true,
      adminOnly: true,
    },
    { isPublic: false },
    { isPublic: false },
  ];

  const openVerseForAdmin = async () => {
    if (loadingVerse) return;
    setLoadingVerse(true);
    try {
      const res = await fetch('/api/verseAccess', { headers: getAdminAuthHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      if (data?.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      }
    } catch {
      // Silently ignore network errors; nothing sensitive to leak.
    } finally {
      setLoadingVerse(false);
    }
  };

  return (
    <div className={`relative min-h-screen flex flex-col items-center justify-start pt-16 pb-10 bg-transparent overflow-hidden ${isModern ? 'modern-page' : ''}`}>
      <Blobs dark={dark} />
      <div className="w-full max-w-6xl mx-auto px-2 sm:px-6">
        <Link
          to="/"
          className={`inline-flex items-center gap-2 mb-8 transition-colors text-lg
            text-[#232634] dark:text-[#f9e2af] font-bold
            px-3 py-1 rounded-lg bg-white/40 dark:bg-[#232634]/60 shadow-sm backdrop-blur-sm
            border border-[#e0d7d2] dark:border-[#45475a] sm:static top-4 left-2 z-20
            focus:outline-none focus:ring-2 focus:ring-[#b4befe] focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-[#181825]
            ${isModern ? 'modern-chip' : ''}`}
          style={{ maxWidth: 'fit-content' }}
        >
          <ArrowLeft size={20} />
          <span>Back to Home</span>
        </Link>

        <h2 className="text-5xl font-extrabold mb-12 font-display text-[#232634] dark:text-white">Heartwork</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 justify-center">
          {interests.map((interest, index) => {
            const locked = interest.isPublic && interest.adminOnly && !isAdmin;
            const clickable = interest.isPublic && (!interest.adminOnly || isAdmin);
            const cardContent = (
              <div className="relative z-10 flex flex-col items-center">
                {interest.isPublic ? (
                  <>
                    <img
                      src="/open_book_mockup_clean_transparent fix.png"
                      alt="BookOpen"
                      className="mb-4 mx-auto w-20 h-20 md:w-24 md:h-24 object-contain"
                      draggable={false}
                    />
                    <div className="text-xl md:text-2xl font-bold mb-2 font-display text-[#232634] dark:text-white flex items-center gap-2">
                      {interest.title}
                      {locked && <Lock className="w-4 h-4 text-[#a6adc8] dark:text-[#b4befe]" aria-label="Admin only" />}
                    </div>
                    <div className="text-[#6c6f85] dark:text-[#a6adc8] text-base text-center max-w-[220px] font-sans">{interest.description}</div>
                    {locked && (
                      <div className="mt-2 text-xs font-bold uppercase tracking-wide text-[#a6adc8] dark:text-[#b4befe]">
                        Admin only
                      </div>
                    )}
                  </>
                ) : (
                  <Lock className="w-10 h-10 md:w-12 md:h-12 text-[#a6adc8] dark:text-[#b4befe] opacity-60 mb-4" />
                )}
              </div>
            );
            return (
              <div
                key={index}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                onClick={clickable && interest.adminOnly ? openVerseForAdmin : undefined}
                onKeyDown={clickable && interest.adminOnly ? (e) => { if (e.key === 'Enter' || e.key === ' ') openVerseForAdmin(); } : undefined}
                className={`
                  relative flex flex-col items-center justify-center rounded-2xl
                  h-[210px] md:h-[300px] px-2 py-4
                  w-full max-w-xs mx-auto
                  bg-[rgba(242,205,205,0.75)] dark:bg-[#181825] border border-[rgba(242,205,205,0.45)] dark:border-[#232634]
                  shadow-[0_4px_24px_0_rgba(242,205,205,0.18)] dark:shadow-none
                  backdrop-blur-md
                  ${clickable ? "hover:border-[#b4befe] hover:shadow-lg cursor-pointer" : "opacity-70"}
                  transition-all duration-300 group
                  ${isModern ? 'modern-surface' : ''}
                `}
                style={{
                  boxShadow: clickable ? (dark ? "0 4px 32px 0 rgba(80, 150, 255, 0.10)" : "0 4px 32px 0 rgba(242,205,205,0.18)") : undefined,
                }}
              >
                {cardContent}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}